import React, { useCallback } from "react";
import Button from "../Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import type { TreeNodeData } from "./TreeView";

interface TreeNodeProps<T extends TreeNodeData = TreeNodeData> {
  node: T;
  level: number;
  selectedId?: string | number;
  expandedIds: Set<string | number>;
  onSelect?: (_node: T) => void;
  onToggleExpand?: (_nodeId: string | number) => void;
  renderNode?: (_node: T, _level: number) => React.ReactNode;
  showConnectors?: boolean;
}

function TreeNode<T extends TreeNodeData = TreeNodeData>({
  node,
  level,
  selectedId,
  expandedIds,
  onSelect,
  onToggleExpand,
  renderNode,
  showConnectors = true,
}: TreeNodeProps<T>) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;

  const handleToggleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onToggleExpand && hasChildren) {
        onToggleExpand(node.id);
      }
    },
    [onToggleExpand, hasChildren, node.id]
  );

  const handleSelect = useCallback(() => {
    if (onSelect) {
      onSelect(node);
    }
  }, [onSelect, node]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "Enter":
        case " ":
          e.preventDefault();
          handleSelect();
          break;
        case "ArrowRight":
          e.preventDefault();
          if (hasChildren && !isExpanded && onToggleExpand) {
            onToggleExpand(node.id);
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (hasChildren && isExpanded && onToggleExpand) {
            onToggleExpand(node.id);
          }
          break;
      }
    },
    [handleSelect, hasChildren, isExpanded, onToggleExpand, node.id]
  );

  // Use custom render function if provided, otherwise use default rendering
  if (renderNode) {
    return (
      <div>
        <div
          className={`
            cursor-pointer transition-colors
            ${isSelected ? "bg-gray-700 border-l-2 border-yellow-500" : ""}
          `}
          onClick={handleSelect}
          onKeyDown={handleKeyDown}
          role="treeitem"
          tabIndex={0}
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-selected={isSelected}
          aria-level={level + 1}
          style={{ paddingLeft: `${level * 1.5}rem` }}
        >
          {/* Expand/Collapse button */}
          {hasChildren ? (
            <div className="flex items-center">
              <Button
                variant="ghost"
                onClick={handleToggleExpand}
                className="mr-2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white p-0"
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} size="xs" />
              </Button>
              <div className="flex-1">{renderNode(node, level)}</div>
            </div>
          ) : (
            <div className="flex items-center">
              <div className="mr-2 w-4 h-4" />
              <div className="flex-1">{renderNode(node, level)}</div>
            </div>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div className={showConnectors ? "ml-6 border-l-2 border-gray-600 pl-3" : "ml-6"}>
            {node.children!.map((childNode) => (
              <TreeNode
                key={childNode.id}
                node={childNode as T}
                level={level + 1}
                selectedId={selectedId}
                expandedIds={expandedIds}
                onSelect={onSelect}
                onToggleExpand={onToggleExpand}
                renderNode={renderNode}
                showConnectors={showConnectors}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default rendering
  return (
    <div>
      <div
        className={`
          flex items-center cursor-pointer p-2 rounded-md hover:bg-gray-700 transition-colors
          ${isSelected ? "bg-gray-700 border-l-2 border-yellow-500" : ""}
        `}
        onClick={handleSelect}
        onKeyDown={handleKeyDown}
        role="treeitem"
        tabIndex={0}
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-selected={isSelected}
        aria-level={level + 1}
        style={{ paddingLeft: `${level * 1.5}rem` }}
      >
        {/* Expand/Collapse button */}
        {hasChildren ? (
          <Button
            variant="ghost"
            onClick={handleToggleExpand}
            className="mr-2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white p-0"
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} size="xs" />
          </Button>
        ) : (
          <div className="mr-2 w-4 h-4" />
        )}

        {/* Node label */}
        <span className={`flex-1 ${isSelected ? "text-yellow-400 font-semibold" : "text-white"}`}>
          {node.label}
        </span>
      </div>

      {/* Render children recursively */}
      {hasChildren && isExpanded && (
        <div className={showConnectors ? "ml-6 border-l-2 border-gray-600 pl-3 mt-1" : "ml-6"}>
          {node.children!.map((childNode) => (
            <TreeNode
              key={childNode.id}
              node={childNode as T}
              level={level + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              renderNode={renderNode}
              showConnectors={showConnectors}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default TreeNode;
